import React from 'react';

import { gql, useMutation } from '@apollo/client';

import type { FetchResult, MutationHookOptions } from '@apollo/client';
import type { JobHiringAttendance } from '../type';

type ApprovedAttendance = Pick<
  JobHiringAttendance,
  'id' | 'status' | 'startOperationTime' | 'endOperationTime' | 'breakDurationMinute'
> & {
  jobHiring?: {
    id: string;
    status: string;
    startOperationTime: string;
    endOperationTime: string;
    breakDurationMinute: number;
    operationDurationMinute: number;
  } | null;
};

type ApproveAttendancesData = {
  approveJobHiringAttendances: {
    success: boolean;
    errors?: string[][] | null;
    data?: ApprovedAttendance[] | null;
  };
};

export type ApproveAttendanceInput = { id: string };

type ApproveAttendancesVariables = { attendances: ApproveAttendanceInput[] };

export type ApproveAttendancesResult = {
  success: boolean;
  data?: ApprovedAttendance[] | null;
  errors?: string[][] | null;
  error?: Error | unknown | null;
  result?: FetchResult<ApproveAttendancesData> | null;
};

const approveAttendancesGql = gql(`
  mutation approveJobHiringAttendances($attendances: [ApproveAttendanceInput!]!) {
    approveJobHiringAttendances(attendances: $attendances) {
      success
      errors
      data {
        id
        status
        startOperationTime
        endOperationTime
        breakDurationMinute
        jobHiring {
          id
          status
          startOperationTime
          endOperationTime
          breakDurationMinute
          operationDurationMinute
        }
      }
    }
  }
`);

const useApproveAttendancesMutation = (
  options?: MutationHookOptions<ApproveAttendancesData, ApproveAttendancesVariables>,
) => {
  const [approveAttendancesMutation, mutationResult] = useMutation<ApproveAttendancesData, ApproveAttendancesVariables>(
    approveAttendancesGql,
    options,
  );

  const approveAttendances = React.useCallback(
    async (attendances: ApproveAttendanceInput[]): Promise<ApproveAttendancesResult> => {
      try {
        const result = await approveAttendancesMutation({ variables: { attendances } });

        return {
          success: result.data?.approveJobHiringAttendances.success ?? false,
          data: result.data?.approveJobHiringAttendances.data ?? null,
          errors: result.data?.approveJobHiringAttendances.errors ?? null,
          result,
        };
      } catch (error) {
        return {
          success: false,
          error,
        };
      }
    },
    [approveAttendancesMutation],
  );

  return {
    approveAttendances,
    mutate: approveAttendancesMutation,
    result: mutationResult,
  };
};

export default useApproveAttendancesMutation;
